<template>
  <v-app id="app_route_plan">
    <v-overlay v-model="centerLoading" :scrim="false" class="centered-overlay">
      <v-progress-circular color="#800080" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-container fluid class="pa-0">
      <!-- Main Content -->
      <v-row class="ma-0">
        <v-col cols="12" md="12" class="pa-0">
          <!-- Title Card -->
          <v-card class="custom_card_box mt-2" elevation="1" border="sm opacity-1" rounded="lg">
            <template v-slot:prepend>
              <v-icon>mdi-view-dashboard</v-icon>
              <v-card-title class="font-weight-bold custom_page_main_sub_title ml-3">ROUTE PLAN DETAILS</v-card-title>
            </template>
          </v-card>

          <!-- Main Information Card -->
          <v-card class="pa-4 custom_card_box mt-3" elevation="1" border="sm opacity-1" rounded="lg">
            <!-- Top Section: Profile and Info -->
            <v-row class="ma-0">
              <!-- Left Side: Profile Avatar and Agent Info -->
              <v-col cols="12" xs="12" sm="12" md="12" lg="12" class="pa-0">
                <v-row>
                  <v-col cols="12" xs="12" sm="3" md="2" lg="2" class="d-flex justify-center">
                    <v-avatar size="80" class="custom_avatar">
                      <v-img :src="agentImage" class="user_image_view"></v-img>
                    </v-avatar>
                  </v-col>
                  <v-col cols="12" xs="12" sm="9" md="10" lg="10">
                    <div class="agent-info">
                      <p class="agent-label">SALES REP CODE :</p>
                      <p class="agent-value">{{ routeData.agentName || 'N/A' }}</p>
                    </div>
                    <div class="agent-info mt-2">
                      <p class="agent-label">MOBILE :</p>
                      <p class="agent-value">{{ formatAgentMobile(routeData.agentMobile) }}</p>
                    </div>
                    <div class="mt-3">
                      <v-chip :class="routeStatusChipClass" variant="outlined">
                        <v-icon :color="routeStatusIconColor" size="small">{{ routeStatusIcon }}</v-icon>&nbsp;
                        ROUTE STATUS - &nbsp;<span style="text-transform: uppercase;">{{ routeData.routeStatus || 'Pending' }}</span>
                      </v-chip>
                    </div>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>

            <!-- Divider Line -->
            <v-row class="mt-4">
              <v-col cols="12">
                <v-divider thickness="1"></v-divider>
              </v-col>
            </v-row>

            <!-- Read-only Fields Section -->
            <v-row class="mt-4">
              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="routeData.routeName" 
                  label="Route Name" 
                  readonly
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_class_view custom_input_field_color"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="routeData.areaCode" 
                  label="Area" 
                  readonly
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_class_view custom_input_field_color"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="routeData.assignDate" 
                  label="Assign Date" 
                  readonly
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_class_view custom_input_field_color"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="routeData.totalOutstanding" 
                  label="Total Outstanding" 
                  readonly
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_class_view custom_input_field_color"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field 
                  v-model="routeData.routeReason" 
                  label="Reason" 
                  readonly
                  variant="outlined" 
                  density="comfortable"
                  class="custom_input_field_class_view custom_input_field_color"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- Divider before Dealer List -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-divider thickness="1"></v-divider>
              </v-col>
            </v-row>

            <!-- Dealer List Table Section -->
            <v-row class="mt-4">
              <v-col cols="12">
                <div class="mb-4">
                  <p class="custom_page_main_sub_title font-weight-bold">DEALER LIST</p>
                  <p class="custom_page_main_sub_title mt-1" style="opacity: 0.6; font-size: 13px;">
                    The following table shows all the dealers related to the above-mentioned route.
                  </p>
                </div>

                <!-- Search Bar -->
                <v-text-field 
                  v-model="search" 
                  append-inner-icon="mdi-magnify" 
                  density="comfortable"
                  class="mb-4 custom_input_field_color"
                  clearable 
                  hide-details 
                  label="Search dealer" 
                  placeholder="Search by dealer name or code"
                  variant="outlined"
                ></v-text-field>

                <!-- Dealer Table -->
                <v-data-table 
                  :headers="headers" 
                  :items="dealerData" 
                  :search="search" 
                  density="comfortable" 
                  class="custom_table_normal"
                  hover
                >
                  <template v-slot:[`item.dealer_info`]="{ item }">
                    <div class="py-2">
                      <div class="dealer-name font-weight-bold">{{ item.outlet_name || 'N/A' }}</div>
                      <div class="reg-num">Dealer Code: {{ item.dealer_code || item.outlet_id || item.id || 'N/A' }}</div>
                    </div>
                  </template>

                  <template v-slot:[`item.outstanding`]="{ item }">
                    <span class="table-text">Rs. {{ formatNumber(item.outstanding || 0) }}</span>
                  </template>

                  <template v-slot:[`item.address`]="{ item }">
                    <span class="table-text">{{ item.address || 'N/A' }}</span>
                  </template>

                  <template v-slot:[`item.visited_date`]="{ item }">
                    <span class="table-text">{{ formatDate(item.visited_date) }}</span>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <v-chip 
                      v-if="item.status === 'Visited'" 
                      class="custom_green_chip" 
                      size="small" 
                      variant="outlined"
                    >
                      <v-icon start size="small">mdi-check</v-icon> VISITED
                    </v-chip>

                    <v-chip 
                      v-if="item.status === 'Not Visited'" 
                      class="custom_red_chip" 
                      size="small" 
                      variant="outlined"
                    >
                      <v-icon start size="small">mdi-close-circle-outline</v-icon> NOT VISITED
                    </v-chip>

                    <v-chip
                      v-if="item.status === 'Rejected'"
                      class="custom_blue_chip"
                      size="small"
                      variant="outlined"
                    >
                      <v-icon start size="small">mdi-cancel</v-icon> REJECTED
                    </v-chip>
                    
                    <v-chip 
                      v-if="!item.status || item.status === 'Pending'" 
                      class="custom_pending_chip" 
                      size="small" 
                      variant="outlined"
                    >
                      <v-icon start size="small">mdi-clock-outline</v-icon> PENDING
                    </v-chip>
                  </template>

                </v-data-table>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" location="top right" timeout="3000">
        {{ snackbarMessage }}
      </v-snackbar>
    </v-container>
  </v-app>
</template>

<script>
import axios from 'axios';
import { mapGetters } from "vuex";
import { getPermissions } from "@/plugins/permissions";

export default {
  name: 'RoutePlanView',
  data() {
    return {
      centerLoading: false,
      search: '',
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      
      // Route data
      routeData: {
        routeName: '',
        areaCode: '',
        assignDate: '',
        totalOutstanding: 'Rs. 0.00',
        routeReason: '',
        agentName: '',
        agentMobile: '',
        agentImage: '',
        routeStatus: ''
      },
      
      // Dealer data
      dealerData: [],

      // Table headers
      headers: [
        { title: 'DEALER NAME', key: 'dealer_info', width: '25%', align: 'start', sortable: true },
        { title: 'OUTSTANDING', key: 'outstanding', width: '15%', align: 'start', sortable: true },
        { title: 'ADDRESS', key: 'address', width: '28%', align: 'start', sortable: true },
        { title: 'VISITED DATE', key: 'visited_date', width: '16%', align: 'start', sortable: true },
        { title: 'STATUS', key: 'status', width: '16%', align: 'start', sortable: false }
      ],
      
      permissions: {},
      routeId: null
    };
  },
  
  computed: {
    ...mapGetters(['sharedDataRoutes']),

    routeStatusChipClass() {
      const status = this.normalizeStatus(this.routeData.routeStatus);
      if (status === 'Visited') return 'custom_green_chip';
      if (status === 'Not Visited') return 'custom_red_chip';
      if (status === 'Rejected') return 'custom_blue_chip';
      return 'custom_pending_chip';
    },

    routeStatusIcon() {
      const status = this.normalizeStatus(this.routeData.routeStatus);
      if (status === 'Visited') return 'mdi-check-circle-outline';
      if (status === 'Not Visited') return 'mdi-close-circle-outline';
      if (status === 'Rejected') return 'mdi-cancel';
      return 'mdi-clock-outline';
    },

    routeStatusIconColor() {
      const status = this.normalizeStatus(this.routeData.routeStatus);
      if (status === 'Visited') return '#1B5E20';
      if (status === 'Not Visited') return '#C62828';
      if (status === 'Rejected') return '#0D47A1';
      return '#EF6C00';
    },

    agentImage() {
      if (this.routeData.agentImage) {
        if (this.routeData.agentImage.startsWith('http')) {
          return this.routeData.agentImage;
        }
        return `${process.env.VUE_APP_API_BASE_URL}/SFA/public/${this.routeData.agentImage}`;
      }
      return 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxMDAiIGhlaWdodD0iMTAwIiB2aWV3Qm94PSIwIDAgMTAwIDEwMCI+PHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNlMGVlZjAiLz48Y2lyY2xlIGN4PSI1MCIgY3k9IjQwIiByPSIxNSIgZmlsbD0iIzkwYThhZCIvPjxwYXRoIGQ9Ik0yMCA3MCBMMjAgNjAgQzIwIDUwIDQwIDUwIDUwIDUwIEM2MCA1MCA4MCA1MCA4MCA2MCBMODAgNzAiIGZpbGw9IiM5MGE4YWQiLz48L3N2Zz4=';
    }
  },
  
  mounted() {
    const dashboardId = 8;
    this.permissions = getPermissions(dashboardId);
    
    if (this.sharedDataRoutes && this.sharedDataRoutes.id) {
      this.routeId = this.sharedDataRoutes.id;
      console.log('Route ID from sharedDataRoutes:', this.routeId);
      this.fetchRouteDetails();
    } else {
      this.snackbarMessage = 'No route data found';
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  },
  
  methods: {
    async fetchRouteDetails() {
      this.centerLoading = true;
      const token = localStorage.getItem('token');
      const parsedUserId = Number.parseInt(this.sharedDataRoutes?.id, 10);
      const userId = Number.isNaN(parsedUserId) ? 1 : parsedUserId;

      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/route/v1/${userId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        console.log('API Response (route details):', response.data);

        let route = null;
        const payload = response?.data?.data ?? response?.data;

        if (payload && !Array.isArray(payload) && typeof payload === 'object') {
          route = payload.route || payload;
        } else if (Array.isArray(payload)) {
          route = payload.find(r => Number.parseInt(r.id, 10) === Number.parseInt(this.routeId, 10));
        }

        if (route) {
          console.log('Found route data:', route);
          
          const dealers = route.dealers || route.selected_outlets || [];

          // Calculate total outstanding from dealers
          const totalOutstanding = dealers.reduce((sum, dealer) => {
            return sum + (parseFloat(dealer.total_outstanding ?? dealer.outstanding) || 0);
          }, 0);
          
          // Get sales rep mobile number from supported response variants
          let agentMobile = 'N/A';
          if (route.repMobile) {
            agentMobile = route.repMobile;
          } else if (route.agent?.mobile_number) {
            agentMobile = route.agent.mobile_number;
          } else if (route.saleRep_mobile) {
            agentMobile = route.saleRep_mobile;
          } else if (route.saleRep?.mobile_number) {
            agentMobile = route.saleRep.mobile_number;
          }
          
          this.routeData = {
            routeName: route.route_name || route.name || 'N/A',
            areaCode: route.area_code || 'N/A',
            assignDate: route.assign_date ? this.formatDate(route.assign_date) : 'N/A',
            totalOutstanding: `Rs. ${totalOutstanding.toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
            routeReason: route.reason || route.route_reason || 'N/A',
            agentName: route.saleRep_name || route.sale_rep_name || route.agent?.name || route.agent_name || route.saleRep_code || 'N/A',
            agentMobile: agentMobile,
            agentImage: route.repImage || route.agent?.user_image || route.agent_image || '',
            routeStatus: this.normalizeStatus(route.route_status) || this.calculateOverallStatus(dealers)
          };
          
          // Map dealer data
          this.dealerData = dealers.map(dealer => {
            const outletName = dealer.dealer_name || dealer.outlet_name || 'N/A';
            const dealerCode = dealer.dealer_code || dealer.outlet_code || dealer.outlet_id || dealer.id || 'N/A';

            return {
              id: dealer.id || dealer.outlet_id,
              outlet_id: dealer.outlet_id || dealer.id,
              dealer_code: dealer.dealer_code || dealer.outlet_code || '',
              outlet_name: outletName,
              // Vuetify table search uses header keys; keep a normalized text field for dealer search.
              dealer_info: `${outletName} ${dealerCode}`,
              outstanding: dealer.total_outstanding ?? dealer.outstanding ?? 0,
              address: dealer.address || 'N/A',
              status: this.normalizeStatus(dealer.status),
              visited_date: dealer.visited_date
            };
          });

          console.log('Route Data after mapping:', this.routeData);
          console.log('Dealer Data after mapping:', this.dealerData);
          
        } else {
          console.error('Route with ID', this.routeId, 'not found in response');
          this.snackbarMessage = 'Route not found';
          this.snackbarColor = 'error';
          this.snackbar = true;
        }
      } catch (error) {
        console.error('Error fetching route details:', error);
        this.snackbarMessage = error.response?.data?.message || 'Failed to load route details';
        this.snackbarColor = 'error';
        this.snackbar = true;
      } finally {
        this.centerLoading = false;
      }
    },
    
    calculateOverallStatus(outlets) {
      if (!outlets || outlets.length === 0) return 'Pending';
      
      const statuses = outlets.map(o => this.normalizeStatus(o.status));
      if (statuses.includes('Visited') && statuses.includes('Not Visited')) {
        return 'Pending';
      }
      if (statuses.every(s => s === 'Visited')) {
        return 'Visited';
      }
      if (statuses.every(s => s === 'Not Visited')) {
        return 'Not Visited';
      }
      return 'Pending';
    },

    normalizeStatus(status) {
      if (!status) return 'Pending';
      const normalized = String(status).trim().toUpperCase();
      if (normalized === 'VISITED') return 'Visited';
      if (normalized === 'NOT VISITED' || normalized === 'NOT_VISITED') return 'Not Visited';
      if (normalized === 'REJECT' || normalized === 'REJECTED') return 'Rejected';
      if (normalized === 'PENDING') return 'Pending';
      return 'Pending';
    },

    formatNumber(value) {
      return parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 });
    },
    
    formatDate(date) {
      if (!date || date === '1970-01-01') return '--';
      const d = new Date(date);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    },

    formatAgentMobile(value) {
      if (!value || value === 'N/A') return 'N/A';

      const mobile = String(value).trim().replace(/\s+/g, '');
      if (!mobile) return 'N/A';
      if (mobile.startsWith('+94')) return mobile;
      if (mobile.startsWith('94')) return `+${mobile}`;
      if (mobile.startsWith('0')) return `+94${mobile.slice(1)}`;
      return `+94${mobile}`;
    },

    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.custom_page_main_title {
  font-size: 24px !important;
  font-weight: 900 !important;
  color: #4b5162 !important;
  margin-bottom: 4px;
  letter-spacing: 0.5px;
}

.custom_page_main_sub_title {
  font-size: 16px !important;
  color: #757575 !important;
}

.custom_card_box {
  font-family: var(--font-family, 'Inter', 'Roboto', sans-serif) !important;
  border-radius: 8px !important;
  box-shadow: 0px 4px 18px 0px #0000001F !important;
  background-color: #ffffff;
}

.custom_avatar {
  border: 2px solid #e0e0e0;
}

.user_image_view {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.agent-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.agent-label {
  font-weight: 600;
  font-size: 14px;
  color: #757575;
  margin: 0;
}

.agent-value {
  font-weight: 600;
  font-size: 16px;
  color: #05146F;
  margin: 0;
}

.custom_input_field_color {
  .v-field__input {
    color: #333333 !important;
  }
}

.custom_input_field_color :deep(.v-label) {
  font-size: 14px;
  font-weight: 500;
  color: #444;
}

.custom_input_field_class_view {
  background-color: #fafafa;
}

.custom_table_normal {
  border-top: 1px solid #e0e0e0;
}

.custom_table_normal :deep(thead th) {
  font-weight: bold !important;
  color: #616161 !important;
  background-color: #fafafa !important;
  text-transform: uppercase;
  font-size: 13px !important;
  border-bottom: 2px solid #EEEEEE !important;
}

.custom_table_normal :deep(.v-data-table__td) {
  border-bottom: 1px solid #F0F0F0 !important;
}

.dealer-name {
  color: #2C3E50;
  font-weight: 600;
  font-size: 14px;
}

.reg-num {
  color: #2196F3;
  font-size: 12px;
  font-weight: 500;
}

.table-text {
  color: #555;
  font-size: 13px;
}

.custom_green_chip,
.custom_green_chip.v-chip--variant-outlined {
  background-color: #E8F5E9 !important;
  border-color: #4CAF50 !important;
  color: #1B5E20 !important;
}

.custom_red_chip,
.custom_red_chip.v-chip--variant-outlined {
  background-color: #FFEBEE !important;
  border-color: #F44336 !important;
  color: #C62828 !important;
}

.custom_pending_chip,
.custom_pending_chip.v-chip--variant-outlined {
  background-color: #FFF3E0 !important;
  border-color: #FF9800 !important;
  color: #EF6C00 !important;
}

.custom_blue_chip,
.custom_blue_chip.v-chip--variant-outlined {
  background-color: #E3F2FD !important;
  border-color: #2196F3 !important;
  color: #0D47A1 !important;
}

.custom_btn_secondary {
  background-color: #e0e0e0 !important;
  color: #424242 !important;
}

.centered-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

</style>